import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { publicAPI } from '../utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Info, Loader2 } from 'lucide-react';

export default function Unsubscribe() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState('loading'); // loading | confirm | busy | ok | neutral

  useEffect(() => {
    if (!token) { setState('neutral'); return; }
    publicAPI.alertsUnsubscribeStatus(token)
      .then((r) => setState(r.data?.status === 'active' ? 'confirm' : 'neutral'))
      .catch(() => setState('neutral'));
  }, [token]);

  const confirm = () => {
    setState('busy');
    publicAPI.unsubscribeAlerts(token)
      .then((r) => setState(r.data?.status === 'ok' ? 'ok' : 'neutral'))
      .catch(() => setState('neutral'));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="p-10 text-center max-w-md w-full" data-testid="unsubscribe-card">
        {state === 'loading' && (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-blue-500" />
            <p className="mt-4 text-gray-600">{t('unsubscribe.processing')}</p>
          </>
        )}
        {(state === 'confirm' || state === 'busy') && (
          <>
            <h1 className="text-xl font-bold text-gray-900" data-testid="unsubscribe-confirm-title">{t('unsubscribe.confirmTitle')}</h1>
            <p className="text-gray-600 mt-2">{t('unsubscribe.confirmBody')}</p>
            <Button onClick={confirm} disabled={state === 'busy'} className="mt-5 bg-red-600 hover:bg-red-700" data-testid="unsubscribe-confirm-btn">
              {t('unsubscribe.confirmBtn')}
            </Button>
          </>
        )}
        {state === 'ok' && (
          <>
            <div className="w-16 h-16 bg-emerald-50 rounded-full mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900" data-testid="unsubscribe-success">
              {t('unsubscribe.successTitle')}
            </h1>
            <p className="text-gray-600 mt-2">
              {t('unsubscribe.successBody')}
            </p>
            <Link to="/">
              <Button className="mt-5 bg-blue-600 hover:bg-blue-700" data-testid="unsubscribe-home-btn">
                {t('unsubscribe.homeBtn')}
              </Button>
            </Link>
          </>
        )}
        {state === 'neutral' && (
          <>
            <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto flex items-center justify-center mb-4">
              <Info className="h-10 w-10 text-slate-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900" data-testid="unsubscribe-neutral">{t('unsubscribe.neutralTitle')}</h1>
            <p className="text-gray-600 mt-2">
              {t('unsubscribe.neutralBody')}
            </p>
            <p className="text-sm text-gray-500 mt-3">
              {t('unsubscribe.errorContact')} <a href="mailto:info@trabadju.cv" className="text-blue-600 underline">info@trabadju.cv</a>.
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
